import React, { useState } from 'react';
import { Container, Typography, Box, TextField, Button, Stack, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { trackWaitlistSignup, trackButtonClick } from '../utils/analytics';

const SectionContainer = styled(Box)(({ theme }) => ({
  background: '#064E3B',
  padding: theme.spacing(12, 0),
  position: 'relative',
  overflow: 'hidden'
}));

const WaitlistCard = styled(Box)(({ theme }) => ({
  background: 'rgba(6, 78, 59, 0.3)',
  backdropFilter: 'blur(20px)',
  borderRadius: (theme.shape.borderRadius as number) * 3,
  border: '1px solid rgba(255, 255, 255, 0.1)',
  padding: theme.spacing(6),
  maxWidth: 600,
  margin: '0 auto',
  textAlign: 'center'
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: (theme.shape.borderRadius as number) * 2,
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.3)'
    },
    '&:hover fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.5)'
    },
    '&.Mui-focused fieldset': {
      borderColor: 'white'
    }
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary
  }
}));

const WaitlistSection: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Track form submission attempt
    trackButtonClick('Waitlist Form Submit', 'WaitlistSection');
    
    if (!email) {
      setError('Please enter your email address');
      // Track validation error
      trackButtonClick('Waitlist Validation Error - Empty Email', 'WaitlistSection');
      return;
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      // Track validation error
      trackButtonClick('Waitlist Validation Error - Invalid Email', 'WaitlistSection');
      return;
    }
    
    try {
      setError('');
      
      // Track API call attempt
      trackButtonClick('Waitlist API Call Started', 'WaitlistSection');
      
      // Call our API function
      const response = await fetch('/api/join-waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        setEmail(''); // Clear the email field
        // Track successful waitlist signup
        trackWaitlistSignup(email);
        // Track successful completion
        trackButtonClick('Waitlist Signup Success', 'WaitlistSection');
      } else {
        setError(data.error || 'Failed to join waitlist. Please try again.');
        // Track API error
        trackButtonClick('Waitlist API Error', 'WaitlistSection');
      }
    } catch (error) {
      console.error('Error joining waitlist:', error);
      setError('Network error. Please check your connection and try again.');
      // Track network error
      trackButtonClick('Waitlist Network Error', 'WaitlistSection');
    }
  };

  const benefits = [
    "Early access to RiteReview platform",
    "50% discount on first 3 months",
    "Priority customer support",
    "Exclusive onboarding session"
  ];

  return (
    <SectionContainer>
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <WaitlistCard>
          <RocketLaunchIcon sx={{ fontSize: 64, color: 'white', mb: 3 }} />
          
          <Typography 
            variant="h2" 
            sx={{ 
              color: 'white', 
              mb: 3,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Coming Soon!
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              mb: 4,
              lineHeight: 1.6
            }}
          >
            RiteReview is launching in Q4 2025. Join our exclusive waitlist to be among the first 
            to experience the future of review automation.
          </Typography>

          {!submitted ? (
            <>
              <Box component="form" onSubmit={handleSubmit} sx={{ mb: 4 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="stretch">
                  <StyledTextField
                    fullWidth
                    type="email"
                    label="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => trackButtonClick('Email Field Focus', 'WaitlistSection')}
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                  />
                  
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    onClick={() => trackButtonClick('Join Waitlist', 'WaitlistSection')}
                    sx={{
                      bgcolor: '#FBBF24',
                      color: '#1F2937',
                      px: 4,
                      py: 2,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: '50px',
                      minWidth: { xs: 'auto', sm: 200 },
                      '&:hover': {
                        bgcolor: '#F59E0B',
                        transform: 'translateY(-2px)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Join Waitlist
                  </Button>
                </Stack>
                
                {error && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                    {error}
                  </Alert>
                )}
              </Box>

              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 4 }}>
                Join 2,500+ businesses already on the waitlist
              </Typography>
            </>
          ) : (
            <Box sx={{ mb: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
              <Typography variant="h5" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                You're on the list!
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                We'll notify you as soon as RiteReview launches. Get ready to boost your reviews!
              </Typography>
            </Box>
          )}

          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ color: 'white', mb: 3, fontWeight: 600 }}>
              Early Bird Benefits
            </Typography>
            
            <Stack spacing={2}>
              {benefits.map((benefit, index) => (
                <Stack key={index} direction="row" alignItems="center" spacing={2}>
                  <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    {benefit}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </WaitlistCard>
      </Container>
    </SectionContainer>
  );
};

export default WaitlistSection;